import { GroundingSource } from "./ChatSourcesList";
import { ChatTurn } from "./chatBotTypes";

function historyItemText(item: Record<string, unknown> | undefined): string {
  if (!item) {
    return "";
  }
  const parts = item.parts as Array<{ text?: string }> | undefined;
  const fromParts = parts?.[0]?.text;
  if (typeof fromParts === "string" && fromParts.length > 0) {
    return fromParts;
  }
  if (typeof item.content === "string") {
    return item.content;
  }
  return typeof fromParts === "string" ? fromParts : "";
}

export function mapChatHistoryToTurns(history: unknown[]): ChatTurn[] {
  const turns: ChatTurn[] = [];

  for (let i = 0; i < history.length; i++) {
    const current = history[i] as Record<string, unknown>;

    if (current.role === "user") {
      const userText = historyItemText(current);

      if (current.is_instruction || userText.includes("<chatbot_instructions>")) {
        continue;
      }

      const next = history[i + 1] as Record<string, unknown> | undefined;
      const isAssistant = next?.role === "model" || next?.role === "assistant";
      const assistantText = isAssistant ? historyItemText(next) : "";

      const historySources: GroundingSource[] =
        isAssistant && Array.isArray(next?.grounding_sources)
          ? (next.grounding_sources as GroundingSource[])
          : [];

      turns.push({
        userTranscript: userText,
        assistantTranscript: assistantText,
        messageId: isAssistant ? (next?.id as string | undefined) : (current.id as string | undefined),
        cacheName: isAssistant ? (next?.cache_name as string | undefined) : (current.cache_name as string | undefined),
        groundingSources: historySources.length > 0 ? historySources : undefined,
        timestamp: (isAssistant ? next?.timestamp : current.timestamp) as string | undefined,
      });

      if (isAssistant) {
        i++;
      }
    } else if (current.role === "model" || current.role === "assistant") {
      turns.push({
        userTranscript: "",
        assistantTranscript: historyItemText(current),
        messageId: current.id as string | undefined,
        cacheName: current.cache_name as string | undefined,
        timestamp: current.timestamp as string | undefined,
      });
    }
  }

  return turns;
}

export function normalizeTranscript(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

/**
 * Turns that could belong to the in-flight recovery (after the last known turn).
 * Prefers messageId anchoring so paginated polls (latest page only) still work when
 * in-memory history is longer than one page.
 */
export function getCandidateRecoveredTurns(
  turns: ChatTurn[],
  historyTurnCount: number,
  lastKnownMessageId?: string,
): ChatTurn[] {
  if (lastKnownMessageId) {
    const idx = turns.findIndex((turn) => turn.messageId === lastKnownMessageId);
    if (idx >= 0) {
      return turns.slice(idx + 1);
    }
  }

  if (historyTurnCount < turns.length) {
    return turns.slice(historyTurnCount);
  }

  // Paginated page shorter than in-memory history, or no messageId anchor: only newest turn
  return turns.length > 0 ? [turns[turns.length - 1]] : [];
}

export function findRecoveredTurn(
  turns: ChatTurn[],
  pendingUserTranscript: string,
  historyTurnCount: number,
  lastKnownMessageId?: string,
): boolean {
  const newerTurns = getCandidateRecoveredTurns(turns, historyTurnCount, lastKnownMessageId);
  const normalizedPending = normalizeTranscript(pendingUserTranscript);

  if (!normalizedPending) {
    return newerTurns.some((turn) => turn.assistantTranscript.trim().length > 0);
  }

  return newerTurns.some(
    (turn) =>
      normalizeTranscript(turn.userTranscript) === normalizedPending &&
      turn.assistantTranscript.trim().length > 0,
  );
}

/** Keep already-loaded older pages; refresh/replace the overlapping recent window. */
export function mergeRecoveredHistory(
  previousTurns: ChatTurn[],
  freshPageTurns: ChatTurn[],
): ChatTurn[] {
  if (previousTurns.length === 0) {
    return freshPageTurns;
  }

  const firstFreshId = freshPageTurns[0]?.messageId;
  if (firstFreshId) {
    const overlapStart = previousTurns.findIndex((turn) => turn.messageId === firstFreshId);
    if (overlapStart >= 0) {
      return [...previousTurns.slice(0, overlapStart), ...freshPageTurns];
    }
  }

  if (previousTurns.length <= freshPageTurns.length) {
    return freshPageTurns;
  }

  const recovered = freshPageTurns[freshPageTurns.length - 1];
  if (!recovered) {
    return previousTurns;
  }

  if (recovered.messageId && previousTurns.some((turn) => turn.messageId === recovered.messageId)) {
    return previousTurns.map((turn) =>
      turn.messageId === recovered.messageId ? recovered : turn,
    );
  }

  return [...previousTurns, recovered];
}
